/* =========================================================================
   UGOITA Plus LP — 動的背景エンジン「金箔のうねる光沢」(WebGL fragment shader)
   - ライブラリ不使用・依存ゼロ。snippet 'ugoita-fx-bg' が出力する
     <canvas class="u-fxbg" data-fx> を DOMContentLoaded 後に自動初期化する。
   - パフォーマンス保証:
       遅延GL生成（IntersectionObserver 初回可視まで context を作らない）/
       低解像度レンダ(0.5x)+DPR上限(1.5) / 画面外・タブ非表示で rAF 停止 /
       prefers-reduced-motion は静止1フレーム / WebGL非対応は data-fx-fallback で静的グラデ /
       同時アクティブ上限(MAX_ACTIVE)。
   - 文字可読性は CSS 側 scrim + コンテンツ z-index:1 と合わせて担保。
   ========================================================================= */
(function () {
  "use strict";
  if (window.UgoitaFX) return;

  var prefersReduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var RENDER_SCALE = 0.5;   // 低解像度レンダ（塗りピクセル 1/4）。うねりは低周波で劣化不可視
  var MAX_ACTIVE = 6;       // 同時にアニメーションする field の上限（複数セクションON対応）

  var VERT = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.0,1.0);}';
  /* 2パターンを u_mode で切替（0=流体[simplex] / 1=地形[value]）。強さは u_strength。色は #08 実値。
     流体は格子（value noiseのセル境界）が広い面で見える問題があり simplex ノイズに置換済み。
     precision は highp（simplex に必須／canvas はモバイル非表示=desktop専用なので highp 前提でOK・mediump フォールバック付き）。 */
  var FRAG = [
    '#ifdef GL_FRAGMENT_PRECISION_HIGH',
    'precision highp float;',
    '#else',
    'precision mediump float;',
    '#endif',
    'uniform vec2 u_res;uniform float u_time;uniform float u_motion;uniform float u_strength;uniform float u_mode;',
    'float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}',
    'float vnoise(vec2 p){vec2 i=floor(p),f=fract(p),u=f*f*(3.0-2.0*f);',
    'return mix(mix(hash(i),hash(i+vec2(1.0,0.0)),u.x),mix(hash(i+vec2(0.0,1.0)),hash(i+vec2(1.0,1.0)),u.x),u.y);}',
    'float vfbm(vec2 p){float s=0.0,a=0.5;for(int k=0;k<4;k++){s+=a*vnoise(p);p*=2.0;a*=0.5;}return s;}',
    'vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}',
    'vec2 mod289(vec2 x){return x-floor(x*(1.0/289.0))*289.0;}',
    'vec3 permute(vec3 x){return mod289(((x*34.0)+1.0)*x);}',
    'float snoise(vec2 v){const vec4 C=vec4(0.211324865405187,0.366025403784439,-0.577350269189626,0.024390243902439);',
    ' vec2 i=floor(v+dot(v,C.yy));vec2 x0=v-i+dot(i,C.xx);',
    ' vec2 i1=(x0.x>x0.y)?vec2(1.0,0.0):vec2(0.0,1.0);',
    ' vec4 x12=x0.xyxy+C.xxzz;x12.xy-=i1;i=mod289(i);',
    ' vec3 p=permute(permute(i.y+vec3(0.0,i1.y,1.0))+i.x+vec3(0.0,i1.x,1.0));',
    ' vec3 m=max(0.5-vec3(dot(x0,x0),dot(x12.xy,x12.xy),dot(x12.zw,x12.zw)),0.0);m=m*m;m=m*m;',
    ' vec3 x=2.0*fract(p*C.www)-1.0;vec3 h=abs(x)-0.5;vec3 ox=floor(x+0.5);vec3 a0=x-ox;',
    ' m*=1.79284291400159-0.85373472095314*(a0*a0+h*h);',
    ' vec3 g;g.x=a0.x*x0.x+h.x*x0.y;g.yz=a0.yz*x12.xz+h.yz*x12.yw;return 130.0*dot(m,g);}',
    'float sfbm(vec2 p){float s=0.0,a=0.5;for(int k=0;k<5;k++){s+=a*snoise(p);p=p*2.0+vec2(13.7,7.3);a*=0.5;}return s*0.5+0.5;}',
    'const vec3 G=vec3(0.659,0.518,0.184);const vec3 GL=vec3(0.792,0.651,0.290);',
    'const vec3 B0=vec3(0.035,0.035,0.043);const vec3 B1=vec3(0.078,0.078,0.086);',
    'vec3 fluidC(vec2 uv){float t=u_time*0.05*u_motion;',
    ' vec2 q=vec2(sfbm(uv*2.0+t),sfbm(uv*2.0-t+5.2));',
    ' float sheen=sfbm(uv*2.0+q*1.2+t);',
    ' float glow=smoothstep(0.35,0.95,sheen);',
    ' return mix(B1,G,glow*u_strength);}',
    'vec3 terrainC(vec2 r){',
    ' float t=u_time*0.2*u_motion;float hz=0.64;float d=hz-r.y;',
    ' vec3 col=mix(B0,B1,r.y);',
    ' if(d>0.001){float persp=0.10/d;float gx=(r.x-0.5)*persp*8.0;float gz=persp*8.0+t*2.0;',
    '  float h=vfbm(vec2(gx*0.5,gz*0.4));float band=abs(fract(gz-h*1.3)*2.0-1.0);',
    '  float ridge=smoothstep(0.88,1.0,1.0-band);float df=smoothstep(0.0,0.10,d);',
    '  col+=ridge*mix(GL,G,clamp(d*1.6,0.0,1.0))*u_strength*df;}',
    ' float gw=smoothstep(0.34,0.0,distance(r,vec2(0.5,hz)));col+=G*gw*0.05;return col;}',
    'void main(){',
    ' vec2 raw=gl_FragCoord.xy/u_res.xy;vec2 uv=raw;uv.x*=u_res.x/u_res.y;',
    ' vec3 c=(u_mode<0.5)?fluidC(uv):terrainC(raw);',
    ' gl_FragColor=vec4(c,1.0);}'
  ].join('\n');

  var fields = [];

  function createField(canvas) {
    if (!canvas || canvas.dataset.fxInit) return null;
    canvas.dataset.fxInit = '1';

    var motion = parseFloat(canvas.getAttribute('data-fx-motion'));
    if (isNaN(motion)) motion = 0.35;
    var modeStr = (canvas.getAttribute('data-fx-mode') || 'fluid').toLowerCase();
    var mode = modeStr === 'terrain' ? 1.0 : 0.0; // 0=流体 / 1=地形（旧'ridge'はフォールバックで流体）
    var strength = parseFloat(canvas.getAttribute('data-fx-strength'));
    if (isNaN(strength)) strength = mode === 1.0 ? 0.6 : 0.7;

    var gl = null, prog = null, uRes = null, uTime = null, uMotion = null, uStrength = null, uMode = null;
    var raf = 0, running = false, visible = false, lastTs = 0, clock = 0, glReady = false, failed = false;
    var io = null, ro = null, rt = 0;

    function compile(type, src) {
      var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
      return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
    }
    function failOut() {
      failed = true;
      canvas.setAttribute('data-fx-fallback', '1'); // CSS で canvas を隠し、wrap の静的グラデを見せる
      teardown();
    }
    function initGL() {
      if (glReady || failed) return glReady;
      try {
        gl = canvas.getContext('webgl', { antialias: false, depth: false, alpha: false, preserveDrawingBuffer: true, powerPreference: 'low-power' })
          || canvas.getContext('experimental-webgl');
      } catch (e) { gl = null; }
      if (!gl) { failOut(); return false; }
      var v = compile(gl.VERTEX_SHADER, VERT), f = compile(gl.FRAGMENT_SHADER, FRAG);
      if (!v || !f) { failOut(); return false; }
      prog = gl.createProgram(); gl.attachShader(prog, v); gl.attachShader(prog, f); gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) { failOut(); return false; }
      gl.useProgram(prog);
      var buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      var loc = gl.getAttribLocation(prog, 'p'); gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      uRes = gl.getUniformLocation(prog, 'u_res');
      uTime = gl.getUniformLocation(prog, 'u_time');
      uMotion = gl.getUniformLocation(prog, 'u_motion');
      uStrength = gl.getUniformLocation(prog, 'u_strength');
      uMode = gl.getUniformLocation(prog, 'u_mode');
      glReady = true; resize();
      return true;
    }
    function resize() {
      if (!glReady) return;
      var dpr = Math.min(window.devicePixelRatio || 1, 1.5) * RENDER_SCALE;
      var r = canvas.getBoundingClientRect();
      var w = Math.max(1, Math.round(r.width * dpr)), h = Math.max(1, Math.round(r.height * dpr));
      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== h) canvas.height = h;
    }
    function draw() {
      if (!glReady) return;
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniform1f(uTime, clock);
      gl.uniform1f(uMotion, motion);
      gl.uniform1f(uStrength, strength);
      gl.uniform1f(uMode, mode);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    function frame(ts) {
      if (!running) return;
      if (!lastTs) lastTs = ts;
      clock += (ts - lastTs) / 1000; lastTs = ts; // アクティブ時のみ加算（停止→再開で飛ばない）
      draw();
      raf = window.requestAnimationFrame(frame);
    }
    function start() {
      if (running || failed) return;
      if (!visible || document.hidden) return;
      if (!initGL()) return;
      if (prefersReduced) { draw(); return; } // 静止1フレーム
      enforceMax(self);
      running = true; lastTs = 0; raf = window.requestAnimationFrame(frame);
    }
    function stop() { running = false; if (raf) { window.cancelAnimationFrame(raf); raf = 0; } }
    function teardown() { if (io) { io.disconnect(); io = null; } if (ro) { ro.disconnect(); ro = null; } }
    function destroy() {
      stop(); teardown();
      var idx = fields.indexOf(self); if (idx >= 0) fields.splice(idx, 1);
      if (gl) { var lc = gl.getExtension && gl.getExtension('WEBGL_lose_context'); if (lc) lc.loseContext(); }
      gl = null; glReady = false; delete canvas.dataset.fxInit;
    }

    var self = {
      canvas: canvas, start: start, stop: stop, destroy: destroy,
      isVisible: function () { return visible; },
      isRunning: function () { return running; }
    };

    io = new IntersectionObserver(function (entries) {
      visible = entries[0].isIntersecting;
      if (visible) start(); else stop();
    }, { rootMargin: '200px 0px', threshold: 0.02 });
    io.observe(canvas);

    if (window.ResizeObserver) {
      ro = new ResizeObserver(function () {
        clearTimeout(rt);
        rt = setTimeout(function () {
          resize();
          if (glReady && !running && (prefersReduced || !visible)) draw(); // 静止状態は再描画して維持
        }, 150);
      });
      ro.observe(canvas);
    }

    fields.push(self);
    return self;
  }

  function enforceMax(keep) {
    var live = fields.filter(function (f) { return f.isRunning(); });
    for (var i = 0; i < live.length && live.length > MAX_ACTIVE; i++) {
      if (live[i] !== keep) { live[i].stop(); live.splice(i, 1); i--; }
    }
  }

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) { fields.forEach(function (f) { f.stop(); }); }
    else { fields.forEach(function (f) { if (f.isVisible()) f.start(); }); }
  });

  function scan(root) {
    var list = (root || document).querySelectorAll('.u-fxbg[data-fx]');
    for (var i = 0; i < list.length; i++) createField(list[i]);
  }

  window.UgoitaFX = { createField: createField, scan: scan };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else { scan(); }

  // Shopify テーマエディタ: セクション再描画に追従
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    if (!e.target.querySelectorAll) return;
    var list = e.target.querySelectorAll('.u-fxbg[data-fx]');
    var kill = fields.filter(function (f) {
      for (var i = 0; i < list.length; i++) if (f.canvas === list[i]) return true;
      return false;
    });
    kill.forEach(function (f) { f.destroy(); });
  });
})();
